import { useCallback, useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useDispatch, useSelector } from "src/redux/hooks";
import { setFeature } from "src/redux/slices/generatorSlice";

interface Props {
  dynamicRows: boolean;
  sectionIndex: number;
}
/**
 * Custom hook to check the time order of rows in a logbook section.
 *
 * @param props - An object containing the following properties:
 *   - dynamicRows: A boolean indicating whether the section contains dynamic rows.
 *   - sectionIndex: A number representing the index of the section to check.
 */
const useCheckTimeOrder = ({ dynamicRows, sectionIndex }: Props) => {
  const dispatch = useDispatch();
  const section = useSelector(
    state => state.generatorSlice.lbentry.sections[sectionIndex],
  );
  const { triggerTimeCheck } = useSelector(
    state => state.generatorFeaturesSlice,
  );
  const { getValues } = useFormContext(); // Get getValues from form context
  const [isEnabled, setIsEnabled] = useState(false);
  useEffect(() => {
    if (!section) {
      setIsEnabled(false);
      return;
    }

    const result =
      dynamicRows &&
      section.rows.some(el => {
        if (el.cols) {
          return Object.values(el.cols).some(col => col.unit === "24lock");
        }
        if (el.subrows) {
          return el.subrows.some(subrow =>
            Object.values(subrow.cols).some(
              (col: any) => col.unit === "24lock",
            ),
          );
        }
        return false;
      });

    setIsEnabled(result);
  }, [dynamicRows, section]);
  /**
   * Runs the time order validation check for the section rows.
   *
   * @returns An array of keys representing rows with time order issues.
   */
  const runCheck = useCallback((): string[] => {
    if (!section) return [];

    const issueKeys: string[] = [];
    let previousTime = "";
    section.rows.forEach(row => {
      const mapper = ([key, col]: [string, any]) => {
        if (col.unit === "24lock") {
          const value = getValues(key)?.time ?? "";
          if (value && value <= previousTime) {
            issueKeys.push(key);
          }
          previousTime = value;
        }
      };

      if (row.cols) {
        Object.entries(row.cols).forEach(mapper);
      } else if (row.subrows) {
        row.subrows.forEach((subrow: any) => {
          Object.entries(subrow.cols).forEach(mapper);
        });
      }
    });
    return issueKeys;
  }, [section, getValues]);

  useEffect(() => {
    if (isEnabled) {
      const issueKeys = runCheck();
      if (issueKeys.length > 0) {
        // Notification("Error", "Time order is not correct", "red");
        const feature: any = {};
        issueKeys.forEach(key => (feature[key] = true));

        dispatch(
          setFeature({
            key: "timeValidation",
            feature,
          }),
        );
      } else {
        dispatch(
          setFeature({
            key: "timeValidation",
            feature: {},
          }),
        );
      }
    }
  }, [dispatch, isEnabled, triggerTimeCheck, runCheck]);
};

export default useCheckTimeOrder;
